import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { ClinicCombobox } from '@/components/shared/ClinicCombobox';
import { MODALITY_FILTER_OPTIONS } from '@/components/studies/worklistDisplay';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, } from '@/components/ui/select';
import { useShowClinicFilter, useStudyClinics } from '@/hooks/use-clinics';
import { useOrgReportTags } from '@/hooks/use-report-tags';
import { STUDIES_REFETCH_INTERVAL_MS } from '@/hooks/use-studies';
import { clinicTagScope } from '@/lib/api/report-tags';
import { cn } from '@/lib/utils';
import { CalendarDays, RefreshCw, Tags, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
export function StudyFilters({ filters, onFiltersChange, activeQuickFilter, onQuickFilterChange, dataUpdatedAt, stacked = false, }) {
    const { t } = useTranslation();
    const { data: clinics } = useStudyClinics();
    const showClinicFilter = useShowClinicFilter();
    // Visual countdown synced to the studies refetch cadence. The constant
    // (STUDIES_REFETCH_INTERVAL_MS in use-studies.ts) drives both the displayed
    // value and the actual TanStack refetchInterval — flipping one without the
    // other is the bug that put 5s on the wire while the UI claimed 25s.
    // Derived from dataUpdatedAt rather than free-running: a filter change
    // re-keys the query, which restarts TanStack's polling interval with an
    // immediate fetch, and a free-running label would stay out of phase with
    // the wire for the rest of the session. Only the clock is state (the
    // react-compiler lint rejects resetting a counter inside the effect); the
    // countdown itself is computed in render.
    const REFRESH_SECONDS = Math.round(STUDIES_REFETCH_INTERVAL_MS / 1000);
    const [now, setNow] = useState(() => Date.now());
    useEffect(() => {
        const interval = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(interval);
    }, []);
    const elapsedSeconds = Math.max(0, Math.floor((now - (dataUpdatedAt ?? 0)) / 1000));
    const refreshCountdown = REFRESH_SECONDS - (elapsedSeconds % REFRESH_SECONDS);
    const modalityFilter = filters.modality || 'all';
    // Diagnosis filter: org tags for the clinic in view (the selected clinic
    // filter, else the user's first clinic). v1 is a plain list, no counts.
    const clinicForTags = filters.clinicCode
        ? clinics?.find((c) => c.code === filters.clinicCode)
        : clinics?.[0];
    const { tags: diagnosisTags } = useOrgReportTags(clinicForTags ? clinicTagScope(clinicForTags.id) : null);
    const tagFilter = filters.tag || 'all';
    // A tag typed via the tag:"..." search prefix may not be an org tag — keep
    // the dropdown honest by surfacing it as a selected entry.
    const showAdHocTag = !!filters.tag &&
        !diagnosisTags.some((dt) => dt.label.toLowerCase() === filters.tag.toLowerCase());
    const handleTagChange = (value) => {
        const newFilters = { ...filters, page: 1 };
        if (value === 'all') {
            delete newFilters.tag;
        }
        else {
            newFilters.tag = value;
        }
        onFiltersChange(newFilters);
    };
    const handleModalityChange = (value) => {
        const newFilters = { ...filters, page: 1 };
        if (value === 'all') {
            delete newFilters.modality;
        }
        else {
            newFilters.modality = value;
        }
        onFiltersChange(newFilters);
        // The status quick-filter ("All Studies" / "Unread" / "Stat") is
        // independent of the modality filter. We deliberately do NOT touch
        // it from here: an earlier version "synced" the two via a partial
        // CT/MR/CR map that fell back to 'all' for US/DX/XA, which caused
        // the parent's quick-filter handler to re-run and clobber the
        // modality we just set (stale-closure read of `filters`). See
        // StudyFilters.test.tsx → "modality dropdown".
    };
    const getDateFilter = () => {
        if (filters.fromDateTime && !filters.toDateTime) {
            const from = new Date(filters.fromDateTime);
            const now = new Date();
            const diffDays = Math.round((now.getTime() - from.getTime()) / (1000 * 60 * 60 * 24));
            if (diffDays <= 1)
                return 'today';
            if (diffDays <= 7)
                return 'week';
            if (diffDays <= 31)
                return 'month';
        }
        if (filters.fromDateTime && filters.toDateTime)
            return 'yesterday';
        return 'all';
    };
    const handleDateChange = (value) => {
        const newFilters = { ...filters, page: 1 };
        const now = new Date();
        switch (value) {
            case 'today':
                newFilters.fromDateTime = new Date(now.setHours(0, 0, 0, 0)).toISOString();
                delete newFilters.toDateTime;
                break;
            case 'yesterday': {
                const y = new Date();
                y.setDate(y.getDate() - 1);
                newFilters.fromDateTime = new Date(y.setHours(0, 0, 0, 0)).toISOString();
                newFilters.toDateTime = new Date(y.setHours(23, 59, 59, 999)).toISOString();
                break;
            }
            case 'week': {
                const w = new Date();
                w.setDate(w.getDate() - 7);
                newFilters.fromDateTime = w.toISOString();
                delete newFilters.toDateTime;
                break;
            }
            case 'month': {
                const m = new Date();
                m.setMonth(m.getMonth() - 1);
                newFilters.fromDateTime = m.toISOString();
                delete newFilters.toDateTime;
                break;
            }
            default:
                delete newFilters.fromDateTime;
                delete newFilters.toDateTime;
        }
        onFiltersChange(newFilters);
    };
    const dateFilter = getDateFilter();
    const hasActiveFilters = modalityFilter !== 'all' ||
        dateFilter !== 'all' ||
        activeQuickFilter === 'unread' ||
        activeQuickFilter === 'stat' ||
        // The header search boxes write these fields; the unified `search` box is the
        // primary term (it superseded `patientName`). Clear must surface whenever any
        // of them is active, not just the legacy patientName filter.
        !!filters.search ||
        !!filters.patientName ||
        !!filters.institutionName ||
        !!filters.referringPhysiciansName ||
        !!filters.reportKeyword ||
        !!filters.tag;
    const clearAllFilters = () => {
        onQuickFilterChange('all');
        onFiltersChange({
            page: 1,
            pageSize: filters.pageSize,
            sortBy: filters.sortBy,
            sortDirection: filters.sortDirection,
        });
    };
    const updateClinicCode = (value) => {
        onFiltersChange({ ...filters, clinicCode: value === 'all' ? undefined : value, page: 1 });
    };
    /** Pill selects: w-max overrides SelectTrigger's w-full; min-w keeps labels readable (SelectTrigger already includes one chevron). */
    const pillBase = 'h-8 w-max min-h-8 shrink-0 gap-1.5 rounded-full border border-border bg-[#161B22] px-3.5 text-xs text-muted-foreground shadow-none transition-colors hover:border-muted-foreground/40 hover:text-foreground data-[state=open]:border-muted-foreground/40 [&>span]:min-w-0 [&>span]:truncate [&>svg:last-child]:h-3 [&>svg:last-child]:w-3 [&>svg:last-child]:opacity-60 pointer-coarse:h-10 pointer-coarse:min-h-10';
    // In the header row (lg+) the group's flex-basis is ~one pill row (28rem),
    // not 0: with a zero basis it never forced a line break, so on 1536-1680px
    // screens it stayed beside the last search box in a ~300px sliver and the
    // pills stacked one per line (a 5-row, 177px header). Now it either fits
    // beside the searches or takes a full row of its own.
    return (_jsxs("div", { className: cn('flex min-w-0 gap-2', 
        // In the sheet the refresh countdown goes under the pills: beside them
        // it squeezed the pill group to one pill per line on a phone.
        stacked ? 'flex-1 flex-col items-stretch' : 'flex-[1_1_28rem] items-center'), "data-testid": "study-filters", children: [_jsxs("div", { className: "flex min-w-0 flex-wrap items-center gap-2", children: [showClinicFilter && clinics && (
                    // Filters by clinic code, which is also what the pill shows. A clinic's
                    // results include the studies other clinics shared with it; the hover
                    // hint says so.
                    _jsx("div", { className: "shrink-0", title: t('filter.clinicIncludesShared'), children: _jsx(ClinicCombobox, { clinics: clinics.map((c) => ({ clinicId: c.code, clinicName: c.code })), value: filters.clinicCode || 'all', onChange: updateClinicCode, allOption: { value: 'all', label: t('filter.allClinics') }, "aria-label": t('filter.allClinics'), "data-testid": "clinic-filter", wrapperClassName: "w-[8.25rem] shrink-0", className: "border-border text-muted-foreground hover:border-muted-foreground/40 hover:text-foreground h-8 rounded-full bg-[#161B22] px-3.5 text-xs shadow-none transition-colors pointer-coarse:h-10" }) })), _jsxs(Select, { value: activeQuickFilter, onValueChange: onQuickFilterChange, children: [_jsx(SelectTrigger, { className: cn(pillBase, 'min-w-[8.25rem]'), children: _jsx(SelectValue, { placeholder: t('filter.allStudies') }) }), _jsxs(SelectContent, { children: [_jsx(SelectItem, { value: "all", children: t('filter.allStudies') }), _jsx(SelectItem, { value: "unread", children: t('filter.unread') }), _jsx(SelectItem, { value: "stat", children: t('filter.statUrgent') })] })] }), _jsxs(Select, { value: modalityFilter, onValueChange: handleModalityChange, children: [_jsx(SelectTrigger, { className: cn(pillBase, 'min-w-[10.5rem]'), children: _jsx(SelectValue, { placeholder: t('filter.allModalities') }) }), _jsxs(SelectContent, { children: [_jsx(SelectItem, { value: "all", children: t('filter.allModalities') }), MODALITY_FILTER_OPTIONS.map((option) => (_jsx(SelectItem, { value: option.value, children: option.label }, option.value)))] })] }), _jsxs(Select, { value: dateFilter, onValueChange: handleDateChange, children: [_jsxs(SelectTrigger, { className: cn(pillBase, 'min-w-[9.25rem]'), children: [_jsx(CalendarDays, { className: "h-3.5 w-3.5 shrink-0 opacity-60" }), _jsx(SelectValue, { placeholder: t('filter.allTime') })] }), _jsxs(SelectContent, { children: [_jsx(SelectItem, { value: "all", children: t('filter.allTime') }), _jsx(SelectItem, { value: "today", children: t('filter.today') }), _jsx(SelectItem, { value: "yesterday", children: t('filter.yesterday') }), _jsx(SelectItem, { value: "week", children: t('filter.pastWeek') }), _jsx(SelectItem, { value: "month", children: t('filter.pastMonth') })] })] }), (diagnosisTags.length > 0 || !!filters.tag) && (_jsxs(Select, { value: tagFilter, onValueChange: handleTagChange, children: [_jsxs(SelectTrigger, { className: cn(pillBase, 'min-w-[10rem]'), children: [_jsx(Tags, { className: "h-3.5 w-3.5 shrink-0 opacity-60" }), _jsx(SelectValue, { placeholder: t('filter.allDiagnoses', { defaultValue: 'Clinical tag' }) })] }), _jsxs(SelectContent, { children: [_jsx(SelectItem, { value: "all", children: t('filter.allDiagnoses', { defaultValue: 'Clinical tag' }) }), showAdHocTag && _jsx(SelectItem, { value: filters.tag, children: filters.tag }), diagnosisTags.map((tag) => (_jsx(SelectItem, { value: tag.label, children: tag.label }, tag.id ?? tag.label)))] })] })), hasActiveFilters && (_jsxs(Button, { variant: "ghost", size: "sm", onClick: clearAllFilters, className: "text-muted-foreground hover:text-foreground h-8 rounded-full px-3 text-xs pointer-coarse:h-10", children: [_jsx(X, { className: "mr-1 h-3 w-3" }), t('filter.clear')] }))] }), _jsxs("div", { className: "text-muted-foreground/60 ml-auto flex shrink-0 items-center gap-1.5 text-[11px]", "data-testid": "auto-refresh-indicator", children: [_jsx("span", { className: "font-mono tracking-wider uppercase", children: stacked ? (t('filter.autoRefresh', { seconds: refreshCountdown })) : (_jsxs(_Fragment, { children: [_jsx("span", { className: "xl:hidden", children: t('filter.autoRefreshShort', { seconds: refreshCountdown }) }), _jsx("span", { className: "hidden xl:inline", children: t('filter.autoRefresh', { seconds: refreshCountdown }) })] })) }), _jsx(RefreshCw, { className: "h-3 w-3", "aria-hidden": true })] })] }));
}
